import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Trophy, Sparkle, Hourglass, Scales, Cloud } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { Hourglass, Scales, Sparkle, Cloud };

export default function Quiz() {
  const { id } = useParams();
  const nav = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [reponses, setReponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/apprentissage/quiz/${id}`)
      .then((r) => setQuiz(r.data))
      .catch(() => { toast.error("Quiz introuvable"); nav("/apprentissage"); });
  }, [id, nav]);

  const submit = async () => {
    if (!quiz) return;
    if (Object.keys(reponses).length < quiz.questions.length) {
      toast.error("Répondez à toutes les questions");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/apprentissage/quiz/${id}/submit`, { reponses });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Erreur soumission"); }
    finally { setSubmitting(false); }
  };

  const reset = () => { setReponses({}); setResult(null); };

  if (!quiz) return <div className="p-12 label-mono">Chargement...</div>;
  const Icon = ICONS[quiz.icone] || Sparkle;

  if (result) return <QuizResult quiz={quiz} result={result} onRetry={reset} />;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
      <Link to="/apprentissage" className="label-mono flex items-center gap-1 mb-6 text-[var(--muted)] hover:text-[var(--signal)]">
        <ArrowLeft size={12} /> Retour au centre d'apprentissage
      </Link>

      <div className="border-l-2 pl-5 mb-8" style={{ borderColor: quiz.couleur }}>
        <div className="label-mono">§ Quiz · {quiz.questions.length} questions</div>
        <h1 className="font-serif text-4xl mt-1 flex items-center gap-3">
          <Icon size={32} weight="duotone" style={{ color: quiz.couleur }} /> {quiz.titre}
        </h1>
        <p className="mt-2 text-[var(--muted)] italic">{quiz.description}</p>
      </div>

      <div className="space-y-px bg-black/15 border border-black/15">
        {quiz.questions.map((q, i) => (
          <div key={q.id} className="bg-[var(--paper)] p-6" data-testid={`quiz-q-${q.id}`}>
            <div className="label-mono mb-3">Question {i + 1} / {quiz.questions.length}</div>
            <h3 className="font-serif text-xl leading-snug">{q.enonce}</h3>
            <div className="mt-4 space-y-2">
              {q.choix.map((c) => {
                const selected = reponses[q.id] === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setReponses({ ...reponses, [q.id]: c.id })}
                    data-testid={`quiz-${q.id}-choice-${c.id}`}
                    className={`w-full text-left p-3 border transition-colors ${
                      selected ? "border-[var(--signal)] bg-[var(--signal-light)]" : "border-black/15 hover:border-black/40"
                    }`}
                  >
                    <span className="font-mono text-xs mr-3 inline-flex items-center justify-center w-6 h-6 border" style={{ borderColor: selected ? quiz.couleur : "#525252", color: selected ? quiz.couleur : "#525252" }}>
                      {c.id.toUpperCase()}
                    </span>
                    <span className={selected ? "font-serif" : ""}>{c.texte}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button onClick={submit} disabled={submitting} data-testid="quiz-submit"
        className="mt-8 w-full bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] py-4 label-mono flex items-center justify-center gap-2 disabled:opacity-50">
        <Sparkle size={14} weight="fill" /> {submitting ? "Calcul du score..." : "Valider mes réponses"} <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function QuizResult({ quiz, result, onRetry }) {
  const Icon = ICONS[quiz.icone] || Sparkle;
  const badgeColor = result.score_pct >= 80 ? "#205D3B" : result.score_pct >= 60 ? "#D92D20" : "#525252";
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10" data-testid="quiz-result">
      <Link to="/apprentissage" className="label-mono flex items-center gap-1 mb-6 text-[var(--muted)] hover:text-[var(--signal)]">
        <ArrowLeft size={12} /> Retour au centre d'apprentissage
      </Link>

      <div className="border-2 p-10 text-center mb-8" style={{ borderColor: badgeColor }}>
        <Trophy size={48} weight="duotone" className="mx-auto" style={{ color: badgeColor }} />
        <div className="label-mono mt-3">§ Résultat · {quiz.titre}</div>
        <div className="font-serif text-7xl font-light mt-2 tabular-nums" style={{ color: badgeColor }}>
          {result.score_pct}<span className="text-3xl text-[var(--muted)]">%</span>
        </div>
        <div className="font-mono text-sm tracking-[0.18em] uppercase mt-2" style={{ color: badgeColor }}>
          Badge {result.badge} · {result.correctes} / {result.total} bonnes réponses
        </div>
      </div>

      <h2 className="font-serif text-2xl mb-4">Détail des réponses</h2>
      <div className="space-y-px bg-black/15 border border-black/15">
        {result.detail.map((d, i) => {
          const q = quiz.questions.find((x) => x.id === d.question_id);
          if (!q) return null;
          const choixCorrect = q.choix.find((c) => c.id === d.bonne_reponse);
          const choixMa = q.choix.find((c) => c.id === d.ma_reponse);
          return (
            <div key={d.question_id} className="bg-[var(--paper)] p-5" data-testid={`quiz-detail-${d.question_id}`}>
              <div className="flex items-start gap-3">
                {d.correcte ? <CheckCircle size={20} weight="fill" className="text-[#205D3B] shrink-0 mt-1" /> : <XCircle size={20} weight="fill" className="text-[var(--signal)] shrink-0 mt-1" />}
                <div className="flex-1">
                  <div className="label-mono">Question {i + 1}</div>
                  <h3 className="font-serif text-lg mt-1">{q.enonce}</h3>
                  <div className="mt-2 text-sm">
                    {!d.correcte && choixMa && <div className="text-[var(--signal)]">Votre réponse : {choixMa.texte}</div>}
                    <div className="text-[#205D3B]">Bonne réponse : {choixCorrect?.texte}</div>
                  </div>
                  <p className="mt-2 text-sm italic text-[var(--muted)] border-l-2 border-black/20 pl-2">{d.explication}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex gap-3 flex-wrap">
        <button onClick={onRetry} data-testid="quiz-retry" className="border border-black px-6 py-3 label-mono hover:bg-black hover:text-[var(--paper)]">
          Recommencer
        </button>
        <Link to="/apprentissage" className="bg-[var(--signal)] text-[var(--paper)] px-6 py-3 label-mono flex items-center gap-2">
          Continuer l'apprentissage <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
